import { StateCreator } from "zustand"
import { mergeDeep } from "../services/functionsGerais"
import { StoreType } from "./AppStore"

type RecursivePartial<T> = {
    [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object | undefined
        ? RecursivePartial<T[P]>
        : T[P]
}

// Aceite automático de pedidos: substitui o antigo booleano `aceitarPedidoAutomatico`.
//  - DESLIGADO: nada automático; o usuário abre o Recebedor para aceitar manualmente.
//  - SEMPRE: aceita/imprime a partir do evento do SSE em qualquer tela aberta (handler global no Cabeçalho).
//  - TELA_ABERTA: DESCONTINUADO. Foi removido das opções do cliente e migra para SEMPRE
//    (ver migrações em ConfigsStore/Cabeçalho). Os gates de runtime usam `autoAceiteLigado`,
//    então enquanto um config ainda tiver este valor ele se comporta IGUAL ao SEMPRE.
export type AceiteAutomaticoValor = "DESLIGADO" | "TELA_ABERTA" | "SEMPRE"

// Fonte única para saber se o aceite automático está ativo (cobre TELA_ABERTA e SEMPRE).
// Também aceita, por retrocompatibilidade, o antigo booleano `aceitarPedidoAutomatico`.
export const autoAceiteLigado = (recebedor: any): boolean => {
    const valor: AceiteAutomaticoValor =
        recebedor?.aceiteAutomatico ??
        (recebedor?.aceitarPedidoAutomatico ? "TELA_ABERTA" : "DESLIGADO")
    return valor !== "DESLIGADO"
}

// Preparo automático com aceite MANUAL: o pedido novo (status 0) espera o operador clicar
// em Aceitar; ao aceitar, ele passa por "Aceito" e é preparado sozinho em seguida (duas
// trocas automáticas). Os já aceitos (1/2) também são preparados sozinhos. Com o aceite
// automático ligado, o preparo automático continua levando o 0 direto para o preparo.
export const aceiteManualComPreparoAuto = (recebedor: any): boolean =>
    !!recebedor?.prepararPedidoAutomatico && !autoAceiteLigado(recebedor)

export type ConfigsType = {
    printer: {
        tamanhoFonteImpressao: number
        tamanhoFonte: number
        agruparViasImpressao: number
        tipoImpressao: string
        impressoras: Array<string>
        layoutPorImpressora?: Record<number, string>
        imprimirLogo?: boolean
        negrito?: boolean
        imprimir99automatico?: boolean
        tamanhoFonteSelect?: string
        tamanhoFonteNavegador?: string
        imprimirComandaCompleta?: boolean
        impressoraComandaCompleta?: string
        imprimirComandasIfood?: boolean
    }
    recebedor: {
        exibicaoMaxima: number
        aceiteAutomatico: AceiteAutomaticoValor
        prepararPedidoAutomatico?: boolean
        imprimir: boolean
        buscarAutomaticamenteXSegundos: number
        efeitoSonoro: boolean
        exibirTempoEspera: boolean
        limitePedidosFinalizados?: number
        imprimirPedidoAutomatico?: boolean
        duracaoEfeitoSonoro?: string
        mostrarEndereco?: boolean
        // Exibe a raia "Aguardando Entregador/Retirada" (status PRONTO = 6) entre
        // Em Preparação e Em Entrega. Desligado: o 6 segue na raia Em Entrega.
        exibirAbaAguardandoEntregador?: boolean
        // Tamanho dos cards/raias do Recebedor, em % (70–130; 100 = padrão). Menor = fonte e
        // card menores (cabem mais pedidos na vertical) e margens laterais maiores. Por estação.
        tamanhoCards?: number
    }
    cardapio: {
        sincronizarCardapio99Automaticamente: boolean
    }
    user?: {
        id: number
        perfil: string
        usuario: string
        parametros: {
            id: number
            estabelecimento: {
                id: number
                nome: string
                endereco: string
                bairro: string
                telefone: string
                tempoEntregaTexto: string
                tempoRetiradaLocal: string
                categoria: string
                tipoCardapio: number
                tempoEntrega: number
                possuiEntrega: boolean
                possuiDelivery: boolean
                possuiEnderecoAtendido: boolean
                apenasRetiradaNoLocal: boolean
                online: boolean
                pausado: boolean
                naoAtendido: boolean
                permiteRetiradaBalcao: boolean
                urlLogo: string
                urlSite: string
                versao: number
                whatsappgrupo: string
                whatsapp: boolean
                bannerEstrategia: boolean
                google: boolean
                panfletos: boolean
                instagram: boolean
                latitude: number
                longitude: number
                precoMedio: number
                possuiPedidoMesa: boolean
                opcoesEntrega: Array<string>
                situacao: number
                solicitarComanda: boolean
                somarQtdItens: boolean
                fazerZoomExibicaoFoto: boolean
                adicionarItensNaTelaInicial: boolean
                permiteFormaPagamentoMesa: boolean
                visivel: boolean
                permitirDesconectarMesa: boolean
                manterEnderecoCliente: boolean
                tipoSelecaoEndereco?: number
            }
            aceitarPedidoAutomatico: boolean
            impressaoQz: boolean
            impressaoAlternada: boolean
            novaTela: boolean
            viasImpressao: number
            modoIzza: number
            deliveryAtivo: boolean
            tempoRetiradaLocal: string
            valorMinimoPedido: number
            valorMinimoRetiradaNoLocal: number
            minimoEntregaGratis: number
            tempoEntregaTexto: string
            fusoHorario: number
            entregaGratis: boolean
            minimoSelosGerar: number
            maximoSelosGerar: number
            quantideMaximaMensal: number
            bonificarIndicacaoAmigos: boolean
            bonificarPesquisaSatisfacao: boolean
            vencimentoMensal: boolean
            corPrimaria: string
            corSecundaria: string
            appNome: string
            situacaoPagamento: string
            maximoRegistroSelosVirtuaisDiarios: number
            maximoRegistroSelosImpressosDiarios: number
            enderecoRetiradaFuiDelivery: string
            ativarFuiDelivery: boolean
            ativarIndiana: boolean
            ativarMvarandas: boolean
            ativaEcletica: boolean
            idPartnerSaipos: string
            codStoreSaipos: string
            ativaSaipos: boolean
            clientIdGetnet: string
            secretIdGetnet: string
            sellerIdGetnet: string
            ativaGetnet: boolean
            pontuarNoDelivery: boolean
            pontuarNoResgate: boolean
            resgatarAutomaticoNoDelivery: boolean
            registrarValorConsumo: boolean
            exibirProdutoPremio: boolean
            valorMinimoParaUsarCupomDesconto: number
            alertarOffline: boolean
            exigirConfirmacaoPedido: boolean
            separadorQuantidadeImpressao: string
            imprimirQuantidadeUm: boolean
            imprimirQuantidadeUmOpcoes: boolean
            imprimirCep: boolean
            possuiModuloPedidosMesa: boolean
            imprimirCidade: boolean
            imprimirClienteNovo: boolean
            imprimirCpf: boolean
            imprimirTotalPorProduto: boolean
            imprimirCategoria: boolean
            imprimirRodape: boolean
            imprimirDataNascimento: boolean
            imprimirQtdPedidos: boolean
            imprimirDescricaoProdutoPedido: boolean
            imprimirDescricaoItemPedido: boolean
            imprimirValorOpcionais: boolean
            imprimirFormaPagamentoTopo: boolean
            imprimirItemAgrupado: boolean
            imprimirLinhaPorProduto: boolean
            imprimirSubTotal: boolean
            imprimirEntregador: boolean
            registroMultiplo: boolean
            validarMaximoDiarioDelivery: boolean
            considerarTaxaEntregaCalculoSelos: boolean
            considerarTaxaEntregaPedidoMinimo: boolean
            tipoImpressao: number
            appPrincipal: string
            apenasRetiradaNoLocal: boolean
            pontuarPorProduto: boolean
            categorizarClientes: boolean
            exigirCpfParaDesconto: boolean
            exigirFacebookEndUnicoParaDesconto: boolean
            exigirEnderecoUnicoParaDesconto: boolean
            seloDuploAtivado: boolean
            permiteMinimoEntregaPorBairro: boolean
            permiteTaxaEntregaNoturna: boolean
            horaInicioTurnoNoite: number
            horaInicioTurnoDia: number
            fatorMultiplicadorSelos: number
            permiteResgatarCartelaIncompleta: boolean
            exibirAdicionaisCadastroCardapio: boolean
            exibirConfiguracaoAvancadaProduto: boolean
            reduzirTamanhoFotoCardapio: boolean
            comissaoPagamentoOnline: number
            qntTotalMensagensWhatsApp: number
            exigirValidacaoTelefonePagamentoOnline: boolean
            permitirPagamentoOnlineRetirada: boolean
            colunasPorLinhaImpressao: number
            agruparViasImpressao: boolean
            ativarClearSale: boolean
            bloquearPagamentoOnlinePrimeiraCompra: boolean
            ignorarPedioMinimoNoResgate: boolean
            tocarNovoPedidoSite: boolean
            urlSiteAplicativo: string
            calculoEntrega: number
            solicitarComanda: boolean
            somarQtdItens: boolean
            fazerZoomExibicaoFoto: boolean
            adicionarItensNaTelaInicial: boolean
            permiteFormaPagamentoMesa: boolean
            exibirRegistroPedidos: boolean
            possuiModuloEntregadores: boolean
            possuiIntegracaoUber: boolean
            possuiIntegracaoFlux?: boolean
            possuiIntegracaoPickngo?: boolean
            idCardapio: number
            mostrarTelefoneSite: boolean
            ativaIntegracaoIfood: boolean
            hashInternaClienteFielIfood: boolean
            listaMotivoCancelamento: Array<{
                cod: string
                description: string
            }>
            tipoEntregaBairro: number
            assinaturaLimitada: boolean
            telaOtimizadaCardapio: boolean
            grupoOpcaoCondicional: boolean
            verificarCardapioAoFinalizarPedido: boolean
            permitirEntregadorAtualizarStatusPedido: boolean
            enviarMensagemDeAvaliacaoPedidoPelaIzza: boolean
            limitarQuantidadeItensCentral: boolean
            atualizarEtapaAoAssociarEntregador: boolean
            imprimirSemAcento: boolean
            imprimirEmNovaAba: boolean
            formatacaoImpressao: string
            tamanhoFonte: number
            larguraFonte: number
            validarEnderecoListaBloqueio: boolean
            espacoInicioLinha: boolean
            imprimirValorDoProduto: boolean
            buscaPedidosOtimizada: boolean
            impedirApenasBebidasPagamentoOnline: boolean
            percentualMaximoBebidasPagamentoOnline: number
            tipoPedidoMesa: number
            permitirEventoAckSemAceitarPedido: boolean
            enviarMensagemWhatsappFiliais: boolean
            permitirApenasPagamentoOnline: boolean
            permitirBuscaPorAdicionais: boolean
            ativaIntegracaoIfoodV2: boolean
            ativarPlugNotas: boolean
            manterEnderecoCliente: boolean
            taxaEntregaVisivelComanda: boolean
            imprimirDescricaoAdicionais: boolean
            tokenMapBox: string
            tokenGoogleMaps: string
            ecomanda: boolean
        }
    }
}

export type ConfigsStoreType = {
    configuracoes: ConfigsType | undefined
    atualizarConfiguracoes: (newConfigs: ConfigsType) => void
}

const configsAntigas = localStorage.getItem("configs")
const parsedConfigsAntigas = configsAntigas ? JSON.parse(configsAntigas) : null

// Migração do aceite automático legado → novo modo unificado.
// No painel antigo o aceite era o booleano `recebedor.aceitarPedidoAutomatico`. Quem já
// tinha o aceite automático MARCADO deve entrar no painel novo como "SEMPRE" (Modo:
// Automático — qualquer tela), e não cair no default "DESLIGADO" (que o apresentaria
// como Manual). Checamos o config CRU, antes do merge com `configsPadrao`, para
// distinguir "nunca definido" (usuário legado) de uma escolha explícita de "DESLIGADO"
// feita no painel novo — esta última já vem re-hidratada do "app-store" e não passa aqui.
if (
    parsedConfigsAntigas?.recebedor &&
    parsedConfigsAntigas.recebedor.aceiteAutomatico == null &&
    parsedConfigsAntigas.recebedor.aceitarPedidoAutomatico
) {
    parsedConfigsAntigas.recebedor.aceiteAutomatico = "SEMPRE"
}

// O modo "Operação automática (só com o Painel)" (TELA_ABERTA) foi descontinuado: quem
// estava nele passa a operar como "Operação automática" (SEMPRE), que aceita/imprime em
// qualquer tela. A migração ao vivo (para o estado re-hidratado do "app-store") fica no
// Cabeçalho.
if (parsedConfigsAntigas?.recebedor?.aceiteAutomatico === "TELA_ABERTA") {
    parsedConfigsAntigas.recebedor.aceiteAutomatico = "SEMPRE"
}

const configsPadrao: RecursivePartial<ConfigsType> = {
    printer: {
        tamanhoFonteImpressao: 14,
        tamanhoFonte: 100,
        agruparViasImpressao: 1,
        tipoImpressao: "",
        // impressoras: [],
    },
    recebedor: {
        exibicaoMaxima: 200,
        aceiteAutomatico: "DESLIGADO",
        imprimir: true,
        buscarAutomaticamenteXSegundos: 180,
        efeitoSonoro: true,
        exibirTempoEspera: true,
    },
    cardapio: {
        sincronizarCardapio99Automaticamente: false,
    },
}

const createConfigsStore: StateCreator<StoreType, [], [], ConfigsStoreType> = (set, get) => {
    // TODO: Remover quando estiver 100% no ar
    // localStorage.removeItem("configs")

    return {
        configuracoes: parsedConfigsAntigas
            ? mergeDeep(configsPadrao, parsedConfigsAntigas)
            : configsPadrao,
        // TODO: Testar
        atualizarConfiguracoes: (newConfigs: Partial<ConfigsType>) => {
            set(prev => ({
                configuracoes: prev.configuracoes
                    ? { ...mergeDeep(prev.configuracoes, newConfigs) }
                    : newConfigs,
            }))

            // Deriva da lista ÚNICA por status (0=Aguardando, 1/2=Aceito) — o store não
            // mantém mais arrays fatiadas.
            const todos = get().arrayTodosPedidos || []
            if (newConfigs.recebedor?.prepararPedidoAutomatico)
                get().prepararPedidosAutomaticos(
                    todos.filter((p: any) => [0, 1, 2].includes(p.status))
                )
            else if (
                newConfigs.recebedor?.aceiteAutomatico &&
                newConfigs.recebedor.aceiteAutomatico !== "DESLIGADO"
            )
                get().aceitarPedidosAutomaticos(todos.filter((p: any) => p.status === 0))
        },
    }
}

export default createConfigsStore
